import React, { useState, useEffect, useRef } from 'react';
import { FaStar } from 'react-icons/fa';
import styled from 'styled-components';
import ReCAPTCHA from 'react-google-recaptcha';
import { ApiServiceImpl } from '../services/api/apiService';
import { Doctor, Review } from '../services/api/types';
import { analytics } from '../services/analytics';

const api = new ApiServiceImpl();

const ReviewsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const ReviewForm = styled.form`
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const StarRating = styled.div`
  display: flex;
  gap: 0.5rem;
  margin: 1rem 0;
`;

const StarWrapper = styled.div<{ active: boolean }>`
  color: ${props => props.active ? '#ffc107' : '#e4e5e9'};
  cursor: pointer;
  font-size: 1.5rem;
  transition: color 0.2s;

  &:hover {
    color: #ffc107;
  }
`;

const VerificationCheckbox = styled.div`
  margin: 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ReviewsList = styled.div`
  display: grid;
  gap: 2rem;
`;

const DoctorSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const DoctorHeader = styled.div`
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
`;

const DoctorInfo = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AverageRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RatingNumber = styled.span`
  font-weight: 500;
  color: #333;
`;

const ReviewCard = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  input, select, textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
  }

  textarea {
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #0056b3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c00;
  padding: 1rem;
  border-radius: 4px;
  margin: 1rem 0;
`;

const ValidationError = styled.div`
  background: #fff3cd;
  color: #856404;
  padding: 0.75rem 1rem;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  margin: 0.5rem 0;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: "⚠️";
    font-size: 1rem;
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 0.75rem 1rem;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  margin: 0.5rem 0;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: "✅";
    font-size: 1rem;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  background: ${props => props.active ? '#007bff' : 'white'};
  color: ${props => props.active ? 'white' : 'black'};
  border-radius: 4px;
  cursor: pointer;

  &:disabled {
    background: #eee;
    cursor: not-allowed;
  }
`;

const SortSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

const RecaptchaContainer = styled.div`
  margin: 1rem 0;
  display: flex;
  justify-content: center;
`;

const Reviews: React.FC = () => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [patientName, setPatientName] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages] = useState(1);
  const [sortBy, setSortBy] = useState<'date' | 'rating'>('date');
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);

  const clearValidationError = () => {
    if (validationError) {
      setValidationError(null);
    }
    if (recaptchaError) {
      setRecaptchaError(null);
    }
  };

  useEffect(() => {
    fetchDoctors();
    fetchReviews();
    
    // Track page view
    analytics.trackPageView('/reviews', 'Doctor Reviews');
  }, [currentPage, sortBy]);


  const fetchDoctors = async () => {
    try {
      const doctorsData = await api.getDoctors();
      setDoctors(doctorsData);
    } catch (err) {
      setError('Error loading doctors. Please try again later.');
    }
  };

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const reviewsData = await api.getReviews();
      setReviews(reviewsData);
    } catch (err) {
      setError('Error loading reviews. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous messages
    setValidationError(null);
    setSuccessMessage(null);
    setRecaptchaError(null);
    
    // Track form submission attempt
    analytics.trackEngagement('form_submit_attempt', 'review_form');
    
    if (!isVerified) {
      setValidationError('Please confirm that you have had surgery with this doctor before submitting a review.');
      analytics.trackEngagement('form_validation_error', 'verification_checkbox');
      return;
    }

    if (!selectedDoctorId) {
      setValidationError('Please select a doctor before submitting a review.');
      analytics.trackEngagement('form_validation_error', 'doctor_selection');
      return;
    }

    if (!recaptchaValue) {
      setRecaptchaError('Please complete the reCAPTCHA verification.');
      analytics.trackEngagement('form_validation_error', 'recaptcha');
      return;
    }

    try {
      const newReview = {
        doctor_id: selectedDoctorId,
        patient_name: patientName || 'Anonymous',
        rating,
        comment,
        author: patientName || 'Anonymous'
      };

      await api.addReview(newReview);
      
      // Track successful review submission
      analytics.trackReviewSubmission(selectedDoctorId, rating, true);
      analytics.trackFormSubmission('review_form', true);
      analytics.trackConversion('review_submitted', rating);
      
      // Show success message
      setSuccessMessage('Thank you! Your review has been submitted successfully.');
      
      // Reset form
      setRating(0);
      setComment('');
      setSelectedDoctorId('');
      setPatientName('');
      setIsVerified(false);
      setRecaptchaValue(null);
      recaptchaRef.current?.reset();
      
      // Refresh reviews
      fetchReviews();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error submitting review:', err);
      setValidationError('Error submitting review. Please try again later.');
      
      // Track failed review submission
      analytics.trackReviewSubmission(selectedDoctorId, rating, false);
      analytics.trackFormSubmission('review_form', false);
      analytics.trackError(`Review submission failed: ${err}`, false);
    }
  };

  const renderStar = (index: number, isActive: boolean) => {
    return (
      <StarWrapper
        key={index}
        active={isActive}
        onClick={() => setRating(index + 1)}
        onMouseEnter={() => setHover(index + 1)}
        onMouseLeave={() => setHover(0)}
      >
        {FaStar({})}
      </StarWrapper>
    );
  };

  // Group reviews by doctor
  const reviewsByDoctor = doctors.map(doctor => ({
    ...doctor,
    reviews: reviews.filter(review => review.doctor_id === doctor.id)
  }));

  const calculateAverageRating = (doctorId: string): number => {
    const doctorReviews = reviews.filter(review => review.doctor_id === doctorId);
    if (doctorReviews.length === 0) return 0;
    
    const sum = doctorReviews.reduce((acc, review) => acc + review.rating, 0);
    return sum / doctorReviews.length;
  };

  if (loading && !reviews.length) {
    return <LoadingSpinner>Loading...</LoadingSpinner>;
  }

  return (
    <ReviewsContainer>
      <h1>Doctor Reviews</h1>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <ReviewForm onSubmit={handleSubmit}>
        <h2>Write a Review</h2>
        
        {validationError && <ValidationError>{validationError}</ValidationError>}
        {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
        
        <FormGroup>
          <label htmlFor="doctor">Select Doctor:</label>
          <select
            id="doctor"
            value={selectedDoctorId}
            onChange={(e) => {
              setSelectedDoctorId(e.target.value);
              clearValidationError();
            }}
            required
          >
            <option value="">Select a doctor...</option>
            {doctors.map(doctor => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name} - {doctor.specialty}
              </option>
            ))}
          </select>
        </FormGroup>

        <FormGroup>
          <label htmlFor="name">Your Name (optional):</label>
          <input
            id="name"
            type="text"
            value={patientName}
            onChange={(e) => {
              setPatientName(e.target.value);
              clearValidationError();
            }}
            placeholder="Anonymous"
          />
        </FormGroup>

        <FormGroup>
          <label>Rating:</label>
          <StarRating>
            {[...Array(5)].map((_, index) => {
              const ratingValue = index + 1;
              return renderStar(index, ratingValue <= (hover || rating));
            })}
          </StarRating>
        </FormGroup>

        <FormGroup>
          <label htmlFor="comment">Your Review:</label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              clearValidationError();
            }}
            required
            rows={4}
          />
        </FormGroup>

        <VerificationCheckbox>
          <input
            type="checkbox"
            id="verification"
            checked={isVerified}
            onChange={(e) => {
              setIsVerified(e.target.checked);
              clearValidationError();
            }}
            required
          />
          <label htmlFor="verification">
            I confirm that I have had surgery with this doctor and this review is based on my personal experience.
          </label>
        </VerificationCheckbox>

        <RecaptchaContainer>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}
            onChange={(value) => {
              setRecaptchaValue(value);
              clearValidationError();
              if (value) {
                analytics.trackRecaptcha('completed');
              }
            }}
            onExpired={() => {
              setRecaptchaValue(null);
              analytics.trackRecaptcha('failed');
            }}
            onErrored={() => {
              setRecaptchaValue(null);
              analytics.trackRecaptcha('failed');
            }}
          />
          {recaptchaError && <ValidationError style={{ marginTop: '0.5rem' }}>{recaptchaError}</ValidationError>}
        </RecaptchaContainer>

        <SubmitButton type="submit" disabled={loading}>
          Submit Review
        </SubmitButton>
      </ReviewForm>

      <div>
        <h2>Recent Reviews</h2>
        
        <FormGroup>
          <label htmlFor="sort">Sort by:</label>
          <SortSelect
            id="sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'date' | 'rating')}
          >
            <option value="date">Most Recent</option>
            <option value="rating">Highest Rating</option>
          </SortSelect>
        </FormGroup>

        {loading ? (
          <LoadingSpinner>Loading reviews...</LoadingSpinner>
        ) : (
          <>
            <ReviewsList>
              {reviewsByDoctor.map(doctor => {
                const averageRating = calculateAverageRating(doctor.id);
                return (
                  <DoctorSection key={doctor.id}>
                    <DoctorHeader>
                      <h2>{doctor.name}</h2>
                      <DoctorInfo>
                        <p>{doctor.specialty}</p>
                        <AverageRating>
                          <RatingNumber>{averageRating.toFixed(1)}</RatingNumber>
                          <StarRating>
                            {[...Array(5)].map((_, index) => (
                              <StarWrapper key={index} active={index < Math.round(averageRating)}>
                                {FaStar({})}
                              </StarWrapper>
                            ))}
                          </StarRating>
                          <span>({reviews.filter(r => r.doctor_id === doctor.id).length} reviews)</span>
                        </AverageRating>
                      </DoctorInfo>
                    </DoctorHeader>
                    
                    {doctor.reviews.map(review => (
                      <ReviewCard key={review.id}>
                        <h3>{review.patient_name}</h3>
                        <StarRating>
                          {[...Array(5)].map((_, index) => (
                            <StarWrapper key={index} active={index < review.rating}>
                              {FaStar({})}
                            </StarWrapper>
                          ))}
                        </StarRating>
                        <p>{review.comment}</p>
                        <small>{new Date(review.created_at).toLocaleDateString()}</small>
                      </ReviewCard>
                    ))}
                  </DoctorSection>
                );
              })}
            </ReviewsList>

            <Pagination>
              <PageButton
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </PageButton>
              <PageButton
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </PageButton>
            </Pagination>
          </>
        )}
      </div>
    </ReviewsContainer>
  );
};

export default Reviews; 